// Decides where to send a user after authentication.
//
// Consultants land in the /pro onboarding flow (a logged-in consultant must
// reach the lead form / dashboard, not the gamified user profile). Everyone
// else goes to their dashboard — unless an explicit funnel target was stored
// before the auth round trip, which always wins.

import { fetchVoucherStats } from 'actions/counselor';

/**
 * True when the authenticated user is a career consultant. Reads the role
 * array surfaced by `GET /auth` (`user.additional.roles`), the linked
 * `consultantId`, or — for a brand-new registration whose backend role grant
 * may not be readable yet — the `?source=pro` registration tag.
 * @param {object} [user] User object from `GET /auth`.
 * @returns {boolean}
 */
export const isConsultantUser = (user) => {
  const roles = user?.additional?.roles || user?.roles;
  if (Array.isArray(roles) && roles.includes('consultant')) return true;
  if (user?.additional?.consultantId) return true;
  try {
    return sessionStorage.getItem('registrationSource') === 'pro';
  } catch {
    return false;
  }
};

export const isAssessmentAdminUser = (user) => {
  const roles = user?.additional?.roles || user?.roles;
  return Array.isArray(roles) && (roles.includes('assessment_admin') || roles.includes('superadmin'));
};

export const isSuperadminUser = (user) => {
  const roles = user?.additional?.roles || user?.roles;
  return Array.isArray(roles) && roles.includes('superadmin');
};

/**
 * Resolve the post-auth redirect path.
 *
 * Async because a consultant's destination depends on whether they have
 * completed the lead form — detected via their voucher count (a counselor with
 * zero vouchers hasn't submitted the application yet).
 * @param {string} [explicitRedirect] A funnel target stored before auth.
 * @param {object} [user] User object from `GET /auth`.
 * @param {Function} [dispatch] Redux dispatch, for the consultant voucher lookup.
 * @returns {Promise<string>} Path to navigate to.
 */
export const resolvePostAuthRedirect = async (explicitRedirect, user, dispatch) => {
  if (explicitRedirect) return explicitRedirect;
  if (isSuperadminUser(user)) return '/dashboard/admin';
  if (isAssessmentAdminUser(user)) return '/dashboard/assessments';
  if (isConsultantUser(user)) {
    let total = 0;
    if (dispatch) {
      try {
        const stats = await dispatch(fetchVoucherStats());
        total = stats?.counts?.total || 0;
      } catch (_) {
        // Non-fatal — send them to the lead form.
      }
    }
    return total > 0 ? '/dashboard/counselor' : '/pro/start/counselor';
  }
  return '/dashboard/user';
};
